import type { AssistantRuntime, Tool } from '@assistant-ui/react';
import { AssistantChatTransport, frontendTools } from '@assistant-ui/react-ai-sdk';
import {
  type ChatRequestOptions,
  type ChatTransport,
  createIdGenerator,
  type HttpChatTransportInitOptions,
  type LanguageModel,
  safeValidateUIMessages,
  streamText,
  type ToolSet,
  type UIMessage,
  type UIMessageChunk,
} from 'ai';
import dayjs from 'dayjs';

import { renderMessages, type ToolWithMessage } from './render-pipeline.tsx';
import type { AtlasMessage } from './types.ts';

type FrontendToolsInput = Record<string, { description?: string; parameters: object }>;

/** Filters tools to only include enabled frontend tools, excluding disabled and backend-only tools. */
const getEnabledTools = (tools: Record<string, Tool>): FrontendToolsInput => {
  return Object.fromEntries(
    Object.entries(tools)
      .filter(([, tool]) => !tool.disabled && tool.type !== 'backend')
      .map(([name, tool]) => [name, { description: tool.description, parameters: tool.parameters ?? {} }]),
  );
};

/**
 * Custom chat transport that handles Atlas-specific message transformation and streaming.
 *
 * Renders the UI message thread to a prose-only model transcript via
 * {@link renderMessages} — tool call/result pairs are read directly
 * off the AI SDK's `ToolUIPart`/`DynamicToolUIPart` (already paired keyed
 * by `toolCallId`), so we never round-trip through `convertToModelMessages`
 * and lose the pairing.
 */
export class AtlasChatTransport extends AssistantChatTransport<AtlasMessage> implements ChatTransport<AtlasMessage> {
  private model: LanguageModel;
  private runtimeRef: AssistantRuntime | undefined;

  /**
   * Whether the user has requested the current agentic run to stop.
   *
   * Owned here (not in React) because the agentic tool loop is driven by
   * the AI SDK's `sendAutomaticallyWhen` continuation, which fires
   * asynchronously across the gap between two requests — a window where
   * the SDK's own `stop()` early-returns (status is transiently `ready`,
   * `activeResponse` is cleared) and does nothing. The transport is the
   * long-lived run-lifecycle owner, so the stop intent lives here and is
   * read by `shouldContinueAgentLoop` to gate the loop. Set via
   * {@link requestStop} on the stop button, cleared via
   * {@link clearStopRequest} on the next user-initiated send/regenerate
   * (loop continuations never pass through those entry points).
   */
  stopRequested = false;

  /** Mark the current agentic run as stopped by the user. */
  requestStop() {
    this.stopRequested = true;
  }

  /** Clear stop intent — called on a genuine user-initiated send/regenerate. */
  clearStopRequest() {
    this.stopRequested = false;
  }

  override setRuntime(runtime: AssistantRuntime) {
    super.setRuntime(runtime);
    this.runtimeRef = runtime;
  }

  constructor(model: LanguageModel, initOptions?: HttpChatTransportInitOptions<UIMessage>) {
    super(initOptions);
    console.info('Creating AtlasChatTransport with model', model);
    this.model = model;
  }

  /**
   * Validates and renders the UI message thread to a prose-only model
   * transcript. {@link renderMessages} owns the recency cap and the
   * per-part rendering; this method only adds validation and the tail
   * system reminder.
   */
  async processMessages(messages: AtlasMessage[], toolRegistry: Record<string, ToolWithMessage>) {
    console.debug('Processing messages for agent transport', { messages });
    const validationResult = await safeValidateUIMessages<AtlasMessage>({ messages });
    if (validationResult.success === false) {
      console.error('Message validation failed:', validationResult.error);
      throw new Error('Message validation failed', { cause: validationResult.error });
    }

    const transcript = [
      ...renderMessages(validationResult.data, { toolRegistry }),
      // {
      //   role: 'system',
      //   content: renderInstructions(<SystemReminder />),
      // } as const,
    ];

    console.debug('Processed messages for agent:', { original: messages, transcript });
    return transcript;
  }

  /**
   * Renders the UI thread to a prose-only transcript and streams the model's response.
   *
   * Tool registration relies on the runtime being attached via {@link setRuntime}; without it
   * no tools are exposed to the LLM and the call still runs as a plain text completion.
   */
  override async sendMessages(
    options: {
      chatId: string;
      messages: AtlasMessage[];
      abortSignal: AbortSignal | undefined;
    } & {
      trigger: 'submit-message' | 'regenerate-message';
      messageId: string | undefined;
    } & ChatRequestOptions,
  ): Promise<ReadableStream<UIMessageChunk>> {
    const modelContext = this.runtimeRef?.thread.getModelContext();
    const enabledTools = getEnabledTools(modelContext?.tools ?? {});
    const tools = frontendTools(enabledTools);
    try {
      // Pass the raw model-context tool registry through so processMessages
      // can read each tool's `message` formatters at transcript-build time.
      const toolRegistry = (modelContext?.tools ?? {}) as Record<string, ToolWithMessage>;
      const processedMessages = await this.processMessages(
        options.messages.filter((message) => message.parts.length > 0),
        toolRegistry,
      );

      const toolNames = Object.keys(enabledTools);
      console.debug('[AtlasChatTransport] sendMessages', {
        timestamp: dayjs().toISOString(),
        trigger: options.trigger,
        toolCount: toolNames.length,
        toolNames,
        tools,
        messageCount: options.messages.length,
        hasModelContext: !!modelContext,
        messages: { input: options.messages, processed: processedMessages },
      });

      const result = streamText({
        model: this.model,
        // biome-ignore lint/suspicious/noExplicitAny: frontendTools returns Schema<unknown>, AI SDK expects FlexibleSchema
        tools: tools as any as ToolSet,
        system: modelContext?.system,
        messages: processedMessages,
        abortSignal: options.abortSignal,
        ...modelContext?.callSettings,
      });

      return result.toUIMessageStream({
        onFinish: (...args: unknown[]) => {
          console.info('AtlasChatTransport stream finished', args);
        },
        originalMessages: options.messages,
        generateMessageId: createIdGenerator({ prefix: 'atlas' }),
        onError: (error: unknown) => {
          console.error('Error in AtlasChatTransport:', error);
          if (error == null) {
            return 'Unknown error';
          }
          if (typeof error === 'string') {
            return error;
          }
          if (error instanceof Error) {
            return error.message;
          }
          return JSON.stringify(error);
        },
      });
    } catch (error) {
      console.error('Error processing messages in AtlasChatTransport:', error);
      throw error;
    }
  }
}
